import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarRange, ChevronRight, Clock, Coffee, FileText, Plus, Sparkles } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { usePunchClock } from "@/hooks/usePunchClock";
import { Button, CardLink, Loader, SectionCard, StatCard, StatusBadge } from "@/components/ui";
import { HolidayList, LeaveBalanceBars, PunchCard } from "@/components/hrms";
import { aed, monthLabel } from "@/lib/format";

const ViewAll = ({ to }) => <CardLink as={Link} to={to}>View all <ChevronRight size={12} /></CardLink>;

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: d } = useData(() => api.dashboard.employee(user.employee_id), [user.employee_id]);
  const clock = usePunchClock(user.employee_id);

  if (!d) return <Loader />;

  const annual = d.leave_balance.find((b) => b.leave_type === "Annual");
  const sick = d.leave_balance.find((b) => b.leave_type === "Sick");
  const now = new Date();

  return (
    <>
      <div className="relative overflow-hidden">
        <div className="absolute -top-16 -right-10 h-40 w-40 rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-blue">
              <Sparkles size={10} className="text-brand-orange" /> {now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <h2 className="mt-1.5 text-xl font-extrabold tracking-tight text-brand-blue">Hello, {d.employee.name.split(" ")[0]}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Punch in, apply for leave and keep track of your payslips.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate("/payroll/slips")}>My payslips</Button>
            <Button variant="primary" icon={<Plus size={14} />} onClick={() => navigate("/leave/apply")}>Apply leave</Button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Annual Leave" value={annual?.balance ?? "—"} sub={`of ${annual?.entitled ?? 30}`} icon={CalendarRange} color="green" onClick={() => navigate("/leave/balance")} />
        <StatCard label="Sick Leave" value={sick?.balance ?? "—"} sub={`of ${sick?.entitled ?? 90}`} icon={Coffee} color="orange" onClick={() => navigate("/leave/balance")} />
        <StatCard label="Pending Requests" value={d.pending_leaves} icon={Clock} onClick={() => navigate("/leave/history")} />
        <StatCard label="Salary Slips" value={d.recent_slips.length} icon={FileText} onClick={() => navigate("/payroll/slips")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-4">
          <PunchCard clock={clock} />
          <SectionCard title="Recent Salary Slips" action={<ViewAll to="/payroll/slips" />}>
            <div className="space-y-1">
              {d.recent_slips.slice(0, 4).map((s) => (
                <Link key={s.id} to="/payroll/slips" className="flex items-center gap-2.5 px-2 py-2 rounded-xl group transition-all hover:shadow-neu-xs">
                  <span className="h-8 w-8 rounded-lg shadow-neu-inset-xs flex items-center justify-center text-brand-blue shrink-0"><FileText size={14} /></span>
                  <span className="flex-1 text-xs font-bold text-brand-blue truncate group-hover:text-brand-orange transition-colors">{monthLabel(s.month)}</span>
                  <span className="text-xs font-extrabold text-brand-blue tabular-nums">AED {aed(s.net_pay, 0)}</span>
                  <StatusBadge status={s.wps_status} />
                </Link>
              ))}
            </div>
          </SectionCard>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <SectionCard title="Leave Balance" action={<ViewAll to="/leave/balance" />}>
            <LeaveBalanceBars balance={d.leave_balance.filter((b) => b.entitled > 0)} />
          </SectionCard>
          <SectionCard title="Upcoming Holidays" action={<ViewAll to="/holidays" />}>
            <HolidayList holidays={d.upcoming_holidays} limit={4} />
          </SectionCard>
        </div>
      </div>
    </>
  );
}
